/**
 * Shared utilities for transcript processing
 */

export interface TranscriptSegment {
  BeginAudioTime: string;
  Content: string;
  Participant: string;
  SegmentId: string;
}

export interface ClickableTranscriptLine {
  text: string;
  startTime: number; // in seconds
  participant: string;
  segments: TranscriptSegment[]; // original segments that make up this line
}

export interface ProcessedTranscript {
  formattedText: string;
  clickableLines: ClickableTranscriptLine[];
  isJson: boolean;
}

/**
 * Process JSON transcript data and format it according to requirements
 * Consolidates consecutive segments from the same participant
 */
export function processJsonTranscript(segments: TranscriptSegment[]): string {
  if (!segments || segments.length === 0) {
    return '';
  }

  const formattedLines: string[] = [];
  let currentParticipant: string | null = null;
  let currentContent: string[] = [];

  for (const segment of segments) {
    const { Participant, Content } = segment;

    // If this is a new participant, flush the previous content
    if (currentParticipant !== null && currentParticipant !== Participant) {
      if (currentContent.length > 0) {
        formattedLines.push(`${currentContent.join(' ')}`);
        currentContent = [];
      }
    }

    // Set the current participant
    currentParticipant = Participant;

    // Add content to current participant's content array
    currentContent.push(Content);
  }

  // Don't forget to add the last participant's content
  if (currentParticipant && currentContent.length > 0) {
    formattedLines.push(`${currentContent.join(' ')}`);
  }

  return formattedLines.join('\n');
}

/**
 * Process JSON transcript data and preserve timing information for clickable navigation
 * Each segment gets its own clickable line with accurate timing (no grouping)
 */
export function processJsonTranscriptWithTiming(
  segments: TranscriptSegment[]
): ProcessedTranscript {
  if (!segments || segments.length === 0) {
    return {
      formattedText: '',
      clickableLines: [],
      isJson: true,
    };
  }

  const formattedLines: string[] = [];
  const clickableLines: ClickableTranscriptLine[] = [];

  // Process each segment individually to preserve accurate timing
  for (const segment of segments) {
    const { Participant, Content, BeginAudioTime, SegmentId } = segment;
    const startTime = parseFloat(BeginAudioTime) || 0;

    // Debug logging for segment structure
    if (segments.length <= 5) {
      // Only log for first few segments to avoid spam
      console.log('🔍 Processing segment:', {
        SegmentId,
        segmentId: (segment as any).segmentId,
        segment_id: (segment as any).segment_id,
        id: (segment as any).id,
        Participant,
        Content: Content.substring(0, 30) + '...',
        BeginAudioTime,
        startTime,
        allKeys: Object.keys(segment),
        rawSegment: segment,
      });
    }

    // Create individual line for each segment
    const lineText = `${Content}`;
    if (lineText.startsWith(`${Participant}: `)) {
      formattedLines.push(`${lineText}`);
    } else {
      formattedLines.push(`${Participant}: ${lineText}`);
    }

    clickableLines.push({
      text: lineText,
      startTime: startTime,
      participant: Participant,
      segments: [segment],
    });
  }

  return {
    formattedText: formattedLines.join('\n'),
    clickableLines,
    isJson: true,
  };
}

/**
 * Fetch and process transcript data from a URL
 * Auto-detects JSON vs text format and processes accordingly
 */
export async function fetchAndProcessTranscript(url: string): Promise<string> {
  console.log('🌐 Fetching transcript from URL:', url);

  const response = await fetch(url);
  console.log('📡 Fetch response:', {
    ok: response.ok,
    status: response.status,
    statusText: response.statusText,
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch transcript: ${response.status} ${response.statusText}`
    );
  }

  // Get the content as text first
  const rawText = await response.text();
  const trimmedText = rawText.trim();

  // Check content-type header
  const contentType = response.headers.get('content-type') || '';
  const contentTypeIsJson =
    contentType.includes('application/json') || contentType.includes('json');

  // Check if content looks like JSON (starts with [ or {)
  const contentLooksLikeJson =
    trimmedText.startsWith('[') || trimmedText.startsWith('{');

  const isJson = contentTypeIsJson || contentLooksLikeJson;

  console.log('🔍 Format detection:', {
    url,
    contentType,
    contentTypeIsJson,
    contentPreview: trimmedText.substring(0, 50) + '...',
    contentLooksLikeJson,
    finalDecision: isJson ? 'JSON' : 'Text',
  });

  let transcriptText: string;

  if (isJson) {
    try {
      console.log('🔄 Attempting to parse JSON data...');
      console.log(
        '📄 Raw text to parse:',
        trimmedText.substring(0, 200) + '...'
      );

      // Process JSON data
      const jsonData = JSON.parse(trimmedText) as TranscriptSegment[];
      console.log('✅ JSON parsing successful!');
      console.log('📄 JSON TRANSCRIPT DATA FETCHED:', {
        length: jsonData.length,
        preview: jsonData.slice(0, 2),
        isArray: Array.isArray(jsonData),
        firstItemKeys: jsonData.length > 0 ? Object.keys(jsonData[0]) : [],
        sampleSegment: jsonData.length > 0 ? jsonData[0] : null,
        hasSegmentId: jsonData.length > 0 ? 'SegmentId' in jsonData[0] : false,
        segmentIdValue: jsonData.length > 0 ? jsonData[0].SegmentId : null,
        hasSegmentIdLower:
          jsonData.length > 0 ? 'segmentId' in jsonData[0] : false,
        segmentIdLowerValue:
          jsonData.length > 0 ? (jsonData[0] as any).segmentId : null,
        hasSegmentIdUnderscore:
          jsonData.length > 0 ? 'segment_id' in jsonData[0] : false,
        segmentIdUnderscoreValue:
          jsonData.length > 0 ? (jsonData[0] as any).segment_id : null,
        hasId: jsonData.length > 0 ? 'id' in jsonData[0] : false,
        idValue: jsonData.length > 0 ? (jsonData[0] as any).id : null,
      });

      transcriptText = processJsonTranscript(jsonData);
      console.log('📝 FORMATTED JSON TRANSCRIPT:', {
        length: transcriptText.length,
        preview: transcriptText.substring(0, 200) + '...',
        fullText: transcriptText,
      });
    } catch (jsonError) {
      console.error('❌ JSON parsing failed:', jsonError);
      console.error(
        '❌ Raw text that failed to parse:',
        trimmedText.substring(0, 500) + '...'
      );
      console.warn('Failed to parse as JSON, treating as text:', jsonError);

      // Try to force parse the JSON even if it looks malformed
      try {
        console.log('🔄 Attempting forced JSON parsing...');
        // Remove any potential BOM or extra characters
        const cleanText = trimmedText.replace(/^\uFEFF/, '').trim();
        const forcedJsonData = JSON.parse(cleanText) as TranscriptSegment[];
        console.log('✅ Forced JSON parsing successful!');
        transcriptText = processJsonTranscript(forcedJsonData);
        console.log('📝 FORMATTED JSON TRANSCRIPT (forced):', {
          length: transcriptText.length,
          preview: transcriptText.substring(0, 200) + '...',
        });
      } catch (forcedError) {
        console.error('❌ Forced JSON parsing also failed:', forcedError);
        transcriptText = rawText;
      }
    }
  } else {
    // Process plain text data
    transcriptText = rawText;
    console.log('📄 TRANSCRIPT TEXT FETCHED:', {
      length: transcriptText.length,
      preview: transcriptText.substring(0, 200) + '...',
    });
  }

  return transcriptText;
}

/**
 * Fetch and process transcript data from a URL with timing information
 * Auto-detects JSON vs text format and processes accordingly
 * Returns ProcessedTranscript with clickable lines for navigation
 */
export async function fetchAndProcessTranscriptWithTiming(
  url: string
): Promise<ProcessedTranscript> {
  console.log('🌐 Fetching transcript from URL:', url);

  const response = await fetch(url);
  console.log('📡 Fetch response:', {
    ok: response.ok,
    status: response.status,
    statusText: response.statusText,
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch transcript: ${response.status} ${response.statusText}`
    );
  }

  // Get the content as text first
  const rawText = await response.text();
  const trimmedText = rawText.trim();

  // Check content-type header
  const contentType = response.headers.get('content-type') || '';
  const contentTypeIsJson =
    contentType.includes('application/json') || contentType.includes('json');

  // Check if content looks like JSON (starts with [ or {)
  const contentLooksLikeJson =
    trimmedText.startsWith('[') || trimmedText.startsWith('{');

  const isJson = contentTypeIsJson || contentLooksLikeJson;

  console.log('🔍 Format detection:', {
    url,
    contentType,
    contentTypeIsJson,
    contentPreview: trimmedText.substring(0, 50) + '...',
    contentLooksLikeJson,
    finalDecision: isJson ? 'JSON' : 'Text',
  });

  if (isJson) {
    try {
      console.log('🔄 Attempting to parse JSON data...');
      console.log(
        '📄 Raw text to parse:',
        trimmedText.substring(0, 200) + '...'
      );

      // Process JSON data
      const jsonData = JSON.parse(trimmedText) as TranscriptSegment[];
      const processedTranscript = processJsonTranscriptWithTiming(jsonData);
      return processedTranscript;
    } catch (jsonError) {
      console.error('❌ JSON parsing failed:', jsonError);
      console.error(
        '❌ Raw text that failed to parse:',
        trimmedText.substring(0, 500) + '...'
      );
      console.warn('Failed to parse as JSON, treating as text:', jsonError);

      // Try to force parse the JSON even if it looks malformed
      try {
        console.log('🔄 Attempting forced JSON parsing...');
        // Remove any potential BOM or extra characters
        const cleanText = trimmedText.replace(/^\uFEFF/, '').trim();
        const forcedJsonData = JSON.parse(cleanText) as TranscriptSegment[];
        console.log('✅ Forced JSON parsing successful!');
        const processedTranscript =
          processJsonTranscriptWithTiming(forcedJsonData);
        console.log('📝 FORMATTED JSON TRANSCRIPT WITH TIMING (forced):', {
          formattedLength: processedTranscript.formattedText.length,
          clickableLinesCount: processedTranscript.clickableLines.length,
          preview: processedTranscript.formattedText.substring(0, 200) + '...',
        });
        return processedTranscript;
      } catch (forcedError) {
        console.error('❌ Forced JSON parsing also failed:', forcedError);
        return {
          formattedText: rawText,
          clickableLines: [],
          isJson: false,
        };
      }
    }
  } else {
    // Process plain text data
    console.log('📄 TRANSCRIPT TEXT FETCHED:', {
      length: rawText.length,
      preview: rawText.substring(0, 200) + '...',
    });

    return {
      formattedText: rawText,
      clickableLines: [],
      isJson: false,
    };
  }
}
